import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { api, coverUri, uploadAudio, uploadDocument, uploadSongCover, PART_VOICES, VOICES, PERFORMANCE_VOICE, PERFORMANCE_LABEL } from "../../lib/api";
import { midiFromIntl, notePt } from "../../lib/piano";
import { Spinner, EmptyState, Button, Field, Sheet, Chip } from "../../components/ui";
import { ProjectField, resolveProjectId } from "../../components/ProjectField";
import { HighlightToggle, FeatureStar, setFeatured, useFeatureToggle } from "../../components/Highlight";
import { Icon } from "../../components/icons";
import { can } from "../../lib/permissions";
import { useAuth } from "../../auth";

const ALL_VOICES = [...PART_VOICES, PERFORMANCE_VOICE];
const voiceLabel = (v) => (v === PERFORMANCE_VOICE ? PERFORMANCE_LABEL : v === "Tutti" ? "Tutti (todos)" : v);

/** One audio track (per voice) uploader — POST /admin/audio. */
function AudioField({ voice, part, onChange }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const pick = async (file) => {
    if (!file) return;
    setBusy(true); setErr("");
    try {
      const up = await uploadAudio(file);
      onChange({ voice, label: voiceLabel(voice), audio_url: "", audio_id: up.audio_id, filename: up.filename });
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };
  return (
    <div className="card" style={{ display: "flex", alignItems: "center", gap: 10, padding: 10 }} data-testid={`audio-field-${voice}`}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontFamily: "var(--f-medium)", fontSize: 14 }}>{voiceLabel(voice)}</div>
        <div className="t-hint" style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{err ? <span style={{ color: "var(--warning)" }}>{err}</span> : part?.filename || "Sem ficheiro"}</div>
      </div>
      {part ? <button className="icon-btn" style={{ width: 32, height: 32 }} title="Remover" onClick={() => onChange(null)} data-testid={`audio-remove-${voice}`}><Icon name="trash" size={15} color="var(--muted)" /></button> : null}
      <label className="chip" style={{ cursor: "pointer" }}>
        <Icon name="upload" size={14} /> {busy ? "A carregar…" : part ? "Substituir" : "Carregar"}
        <input type="file" accept="audio/*,.mp3,.m4a,.aac" hidden onChange={(e) => pick(e.target.files?.[0])} data-testid={`audio-input-${voice}`} />
      </label>
    </div>
  );
}

/** PDF documents of a given kind — POST /admin/documents. */
function DocsField({ kind, documents, onChange, testid }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const pick = async (file) => {
    if (!file) return;
    setBusy(true); setErr("");
    try {
      const up = await uploadDocument(file);
      onChange([...documents, { file_id: up.file_id, title: up.title || up.filename, filename: up.filename, size: up.size, kind }]);
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };
  const remove = (fid) => onChange(documents.filter((d) => d.file_id !== fid));
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }} data-testid={testid}>
      {documents.map((d) => (
        <div key={d.file_id} className="card" style={{ display: "flex", alignItems: "center", gap: 10, padding: 10 }}>
          <Icon name="document-text" size={16} color="var(--muted)" />
          <div style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: 13 }}>{d.title || d.filename}</div>
          <button className="icon-btn" style={{ width: 32, height: 32 }} onClick={() => remove(d.file_id)}><Icon name="trash" size={15} color="var(--muted)" /></button>
        </div>
      ))}
      {err ? <div className="t-hint" style={{ color: "var(--warning)" }}>{err}</div> : null}
      <label className="btn ghost" style={{ cursor: "pointer", alignSelf: "flex-start" }}>
        <Icon name="add" size={15} /> {busy ? "A carregar…" : "Adicionar PDF"}
        <input type="file" accept="application/pdf,.pdf" hidden onChange={(e) => pick(e.target.files?.[0])} />
      </label>
    </div>
  );
}

function SongForm({ id, initial, projects, onClose, onSaved }) {
  const qc = useQueryClient();
  const { user } = useAuth();
  const canAudio = can(user, "audio.manage");
  const canDocs = can(user, "documents.manage");
  const [f, setF] = useState({
    title: initial?.title || "", artist: initial?.artist || "", cover_url: initial?.cover_url || "",
    duration: initial?.duration || "", lyrics: initial?.lyrics || "", notes: initial?.notes || "",
    project_id: initial?.project_id ?? null,
  });
  const [featured, setFeat] = useState(!!initial?.featured);
  const [projectName, setProjectName] = useState(() => projects.find((p) => p.id === initial?.project_id)?.name || "");
  const [parts, setParts] = useState(() => Object.fromEntries((initial?.parts || []).map((p) => [p.voice, p])));
  const [documents, setDocuments] = useState(() => (initial?.documents || []).map(({ file_id, title, filename, size, kind }) => ({ file_id, title, filename, size, kind: kind || "outro" })));
  const [startNotes, setStartNotes] = useState(() => ({ ...(initial?.start_notes || {}) }));
  const [coverBusy, setCoverBusy] = useState(false);
  const [err, setErr] = useState("");

  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v }));
  const setPart = (voice) => (p) => setParts((cur) => { const next = { ...cur }; if (p) next[voice] = p; else delete next[voice]; return next; });
  const byKind = (k) => documents.filter((d) => (d.kind || "outro") === k);
  const setKind = (k) => (list) => setDocuments([...documents.filter((d) => (d.kind || "outro") !== k), ...list]);

  const uploadCover = async (file) => {
    if (!file) return;
    setCoverBusy(true); setErr("");
    try { const up = await uploadSongCover(file); set("cover_url")(up.cover_url || up.url); }
    catch (e) { setErr(e.message); } finally { setCoverBusy(false); }
  };

  const save = useMutation({
    mutationFn: async () => {
      const list = ALL_VOICES.filter((v) => parts[v]).map((v) => ({
        voice: v, label: voiceLabel(v),
        audio_url: parts[v].audio_id ? "" : parts[v].audio_url || "",
        audio_id: parts[v].audio_id || null, filename: parts[v].filename || "",
      }));
      const clean = {};
      VOICES.forEach((v) => { if (midiFromIntl(startNotes[v]) != null) clean[v] = startNotes[v]; });
      const project_id = await resolveProjectId(projects, projectName);
      qc.invalidateQueries({ queryKey: ["projects"] });
      const r = await api(id ? `/admin/songs/${id}` : "/admin/songs", { method: id ? "PUT" : "POST", body: JSON.stringify({ ...f, project_id, ...(canAudio ? { parts: list } : {}), ...(canDocs ? { documents } : {}), start_notes: clean }) });
      const sid = id || r?.id;
      if (sid && featured !== !!initial?.featured) await setFeatured("songs", sid, featured);
      return r;
    },
    onSuccess: onSaved,
    onError: (e) => setErr(e.message),
  });

  const submit = () => { setErr(""); if (!f.title.trim() || !f.artist.trim()) return setErr("Indique o título e o artista."); save.mutate(); };

  return (
    <Sheet title={id ? "Editar música" : "Nova música"} onClose={onClose} testid="song-form" wide>
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <Field label="TÍTULO" value={f.title} onChange={(e) => set("title")(e.target.value)} placeholder="Título da música" data-testid="song-title-input" />
        <Field label="ARTISTA" value={f.artist} onChange={(e) => set("artist")(e.target.value)} placeholder="Artista / compositor" data-testid="song-artist-input" />

        <ProjectField projects={projects} value={projectName} onChange={setProjectName} />

        <Field label="DURAÇÃO" value={f.duration} onChange={(e) => set("duration")(e.target.value)} placeholder="4:30" data-testid="song-duration-input" />

        <div>
          <div className="t-section">Foto</div>
          <div className="t-hint" style={{ marginBottom: 8 }}>Imagem de capa (JPG/PNG). Aparece no repertório, na página da música e nos alinhamentos.</div>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            {f.cover_url ? <img className="cover" src={coverUri(f.cover_url)} alt="" style={{ width: 64, height: 64, borderRadius: "var(--r-sm)" }} /> : <div style={{ width: 64, height: 64, borderRadius: "var(--r-sm)", background: "var(--surface-3)", display: "flex", alignItems: "center", justifyContent: "center" }}><Icon name="image" size={22} color="var(--muted)" /></div>}
            <label className="btn ghost" style={{ cursor: "pointer" }}>
              <Icon name="image" size={16} /> {coverBusy ? "A carregar…" : "Carregar capa"}
              <input type="file" accept="image/*" hidden onChange={(e) => uploadCover(e.target.files?.[0])} data-testid="song-cover-input" />
            </label>
          </div>
        </div>

        <div>
          <div className="t-section">Ficheiros de áudio de ensaio</div>
          <div className="t-hint" style={{ marginBottom: 8 }}>Um MP3 ou M4A/AAC por voz. Ficam privados: os membros apenas os ouvem na aplicação.</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {canAudio ? PART_VOICES.map((v) => <AudioField key={v} voice={v} part={parts[v]} onChange={setPart(v)} />) : <div className="t-hint" style={{ fontSize: 12 }} data-testid="song-audio-locked">Sem permissão para gerir ficheiros áudio.</div>}
          </div>
        </div>

        <div>
          <div className="t-section">Versão de espetáculo</div>
          <div className="t-hint" style={{ marginBottom: 8 }}>Gravação completa como será apresentada em concerto (todas as vozes, com acompanhamento).</div>
          {canAudio ? <AudioField voice={PERFORMANCE_VOICE} part={parts[PERFORMANCE_VOICE]} onChange={setPart(PERFORMANCE_VOICE)} /> : <div className="t-hint" style={{ fontSize: 12 }} data-testid="song-performance-locked">Sem permissão para gerir ficheiros áudio.</div>}
        </div>

        <div>
          <div className="t-section">Notas de entrada</div>
          <div className="t-hint" style={{ marginBottom: 8 }}>A nota em que cada naipe começa a cantar (ex.: C4, E4, G3). Ouvível no piano da página da música.</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }} data-testid="start-notes-editor">
            {VOICES.map((v) => {
              const midi = midiFromIntl(startNotes[v]);
              return (
                <div key={v} className="card" style={{ display: "flex", alignItems: "center", gap: 10, padding: 10 }}>
                  <span style={{ flex: 1, fontFamily: "var(--f-medium)", fontSize: 14 }}>{v}</span>
                  {midi != null ? <span className="t-hint">{notePt(midi)}</span> : (startNotes[v] ? <span className="t-hint" style={{ color: "var(--warning)" }}>nota inválida</span> : null)}
                  <input className="input" style={{ width: 90, textAlign: "center" }} value={startNotes[v] || ""} onChange={(e) => setStartNotes((s) => ({ ...s, [v]: e.target.value.trim() }))} placeholder="ex.: C4" data-testid={`start-note-${v}`} />
                </div>
              );
            })}
          </div>
        </div>

        <div>
          <div className="t-section">Partitura (PDF)</div>
          <div className="t-hint" style={{ marginBottom: 8 }}>Partitura(s) em PDF, para consultar e imprimir.</div>
          {canDocs ? <DocsField kind="partitura" documents={byKind("partitura")} onChange={setKind("partitura")} testid="docs-partitura" /> : <div className="t-hint" style={{ fontSize: 12 }} data-testid="song-docs-locked">Sem permissão para gerir partituras e documentos.</div>}
        </div>
        <div>
          <div className="t-section">Letra PDF</div>
          <div className="t-hint" style={{ marginBottom: 8 }}>Letra em PDF (a letra em texto edita-se mais abaixo).</div>
          {canDocs ? <DocsField kind="letra" documents={byKind("letra")} onChange={setKind("letra")} testid="docs-letra" /> : null}
        </div>
        <div>
          <div className="t-section">Outros documentos (PDF)</div>
          {canDocs ? <DocsField kind="outro" documents={byKind("outro")} onChange={setKind("outro")} testid="docs-outro" /> : null}
        </div>

        <Field as="textarea" label="LETRA" value={f.lyrics} onChange={(e) => set("lyrics")(e.target.value)} placeholder="Letra da música" data-testid="song-lyrics-input" />
        <Field as="textarea" label="NOTAS" value={f.notes} onChange={(e) => set("notes")(e.target.value)} placeholder="Indicações do maestro" data-testid="song-notes-input" />

        <HighlightToggle label={id ? "Destacar música na Home" : "Destacar nova música na Home"} checked={featured} onChange={setFeat} testid="song-featured" />
        {err ? <div style={{ background: "var(--error)", color: "var(--on-error)", padding: 10, borderRadius: "var(--r-md)", fontSize: 13 }} data-testid="song-form-error">{err}</div> : null}
        <Button loading={save.isPending} onClick={submit} data-testid="song-save">GUARDAR</Button>
      </div>
    </Sheet>
  );
}

export default function AdminSongs() {
  const { user: me } = useAuth();
  const canDelete = can(me, "songs.delete");
  const canEdit = can(me, "songs.edit");
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [text, setText] = useState("");
  const [query, setQuery] = useState("");
  const [editing, setEditing] = useState(null); // {id} | {new:true} | null
  const [loadingId, setLoadingId] = useState(null);

  useEffect(() => { const t = setTimeout(() => setQuery(text), 250); return () => clearTimeout(t); }, [text]);

  const q = useQuery({ queryKey: ["songs", query, ""], queryFn: () => api(`/songs?q=${encodeURIComponent(query)}&status=`) });
  const songs = Array.isArray(q.data) ? q.data : q.data?.songs || [];
  const projectsQ = useQuery({ queryKey: ["projects"], queryFn: () => api("/projects") });

  const feature = useFeatureToggle("songs");
  const del = useMutation({
    mutationFn: (id) => api(`/admin/songs/${id}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["songs"] }),
  });

  const openEdit = async (s) => {
    setLoadingId(s.id);
    try { const full = await api(`/songs/${s.id}`); setEditing({ id: s.id, initial: full }); }
    catch { setEditing({ id: s.id, initial: s }); } finally { setLoadingId(null); }
  };
  const onSaved = () => { qc.invalidateQueries({ queryKey: ["songs"] }); qc.invalidateQueries({ queryKey: ["home"] }); setEditing(null); };

  return (
    <>
      <div className="page-header" style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <button className="icon-btn" onClick={() => navigate(-1)}><Icon name="chevron-back" size={22} /></button>
        <h1 className="t-h1" style={{ flex: 1 }}>Repertório</h1>
        {canEdit ? <Button className="auto sm" icon="add" onClick={() => setEditing({ new: true })} data-testid="song-new-button">NOVA MÚSICA</Button> : null}
      </div>
      {q.isLoading ? (
        <Spinner />
      ) : (
        <div className="page ulc-fade" style={{ maxWidth: 900 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, height: 46, borderRadius: 999, background: "var(--surface-2)", border: "1px solid var(--border)", padding: "0 16px", maxWidth: 520, marginBottom: 16 }}>
            <Icon name="search" size={18} color="var(--muted)" />
            <input data-testid="admin-song-search" value={text} onChange={(e) => setText(e.target.value)} placeholder="Pesquisar música…" style={{ flex: 1, background: "none", border: "none", outline: "none", color: "var(--on-surface)", fontSize: 15, fontFamily: "var(--f-regular)" }} />
          </div>
          <div style={{ color: "var(--muted)", fontFamily: "var(--f-medium)", fontSize: 11, letterSpacing: 2, marginBottom: 12 }}>{songs.length} {songs.length === 1 ? "MÚSICA" : "MÚSICAS"}</div>
          {!songs.length ? <EmptyState icon="library" text="Nenhuma música encontrada." /> : null}
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {songs.map((s) => (
              <div key={s.id} className="card" style={{ display: "flex", alignItems: "center", gap: 12, padding: 12 }} data-testid={`admin-song-${s.id}`}>
                <img className="cover" src={coverUri(s.cover_url)} alt="" style={{ width: 48, height: 48, borderRadius: "var(--r-sm)" }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontFamily: "var(--f-medium)", fontSize: 15, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{s.title}</div>
                  <div className="t-hint">{[s.artist, s.project].filter(Boolean).join(" · ")}</div>
                </div>
                {canEdit ? <FeatureStar kind="songs" item={s} toggle={feature} /> : null}
                <button className="icon-btn" onClick={() => navigate(`/song/${s.id}`)} title="Ver como membro" data-testid={`song-preview-${s.id}`}><Icon name="chevron-forward" size={16} color="var(--muted)" /></button>
                {canEdit ? <button className="icon-btn" onClick={() => openEdit(s)} data-testid={`song-edit-${s.id}`}>{loadingId === s.id ? <div className="spinner" style={{ width: 16, height: 16, borderWidth: 2 }} /> : <Icon name="pencil" size={16} />}</button> : null}
                {canDelete ? <button className="icon-btn" onClick={() => window.confirm(`Eliminar "${s.title}"?`) && del.mutate(s.id)} data-testid={`song-delete-${s.id}`}><Icon name="trash" size={16} color="var(--muted)" /></button> : null}
              </div>
            ))}
          </div>
        </div>
      )}

      {editing ? (
        <SongForm
          id={editing.id}
          initial={editing.initial}
          projects={projectsQ.data || []}
          onClose={() => setEditing(null)}
          onSaved={onSaved}
        />
      ) : null}
    </>
  );
}
